import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Button,
  Spinner,
  Card,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Label,
  Checkbox,
  Tooltip,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  PersonAdd24Regular,
  Search24Regular,
  Edit24Regular,
  Delete24Regular,
  Shield24Regular,
  ShieldTask24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  Key24Regular,
  Save24Regular,
  PeopleTeam24Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  ShieldPerson20Regular,
} from '@fluentui/react-icons';
import { accessControlService } from '../../services/accessControlService';
import type {
  UserAccessDto,
  ScreenPermissionDto,
  ScreenDefinitionDto,
  CreateUserAccessRequest,
  UpdateUserAccessRequest,
} from '../../types';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXXL,
    maxWidth: '1400px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalXL,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  searchContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
  },
  searchInput: {
    width: '300px',
  },
  tableContainer: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  adminIcon: {
    marginLeft: tokens.spacingHorizontalS,
    color: tokens.colorBrandForeground1,
    verticalAlign: 'middle',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    justifyContent: 'center',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  permissionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxHeight: '400px',
    overflowY: 'auto',
    padding: tokens.spacingHorizontalS,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  permissionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '5px',
    flexShrink: 0,
  },
  iconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  iconGray: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    padding: '32px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  dialogSurface: {
    maxWidth: '500px',
    width: '90vw',
  },
  actionsHeader: {
    width: '100%',
    textAlign: 'center',
  },
  tableHeaderRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    '& > th': {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightBold,
      fontSize: tokens.fontSizeBase200,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
      ...shorthands.borderBottom('2px', 'solid', tokens.colorNeutralStroke1),
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  },
});

export const AccessControlPage: React.FC = () => {
  const styles = useStyles();

  const [users, setUsers] = useState<UserAccessDto[]>([]);
  const [screens, setScreens] = useState<ScreenDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAccessDto | null>(null);
  const [userPermissions, setUserPermissions] = useState<ScreenPermissionDto[]>([]);

  // Form states
  const [formEmail, setFormEmail] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsAdmin, setFormIsAdmin] = useState(false);
  const [formSaving, setFormSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, screensData] = await Promise.all([
        accessControlService.getUsers(),
        accessControlService.getScreens(),
      ]);
      setUsers(usersData);
      setScreens(screensData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = async () => {
    setFormSaving(true);
    setError(null);
    try {
      const request: CreateUserAccessRequest = {
        email: formEmail,
        displayName: formDisplayName,
        isActive: formIsActive,
        isAdmin: formIsAdmin,
      };
      await accessControlService.createUser(request);
      setCreateDialogOpen(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao criar usuario');
    } finally {
      setFormSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setFormSaving(true);
    setError(null);
    try {
      const request: UpdateUserAccessRequest = {
        displayName: formDisplayName,
        isActive: formIsActive,
        isAdmin: formIsAdmin,
      };
      await accessControlService.updateUser(selectedUser.email, request);
      setEditDialogOpen(false);
      resetForm();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao atualizar usuario');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setFormSaving(true);
    setError(null);
    try {
      await accessControlService.deleteUser(selectedUser.email);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao excluir usuario');
    } finally {
      setFormSaving(false);
    }
  };

  const handleToggleActive = async (email: string) => {
    setError(null);
    try {
      await accessControlService.toggleUserActive(email);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao alterar status');
    }
  };

  const handleToggleAdmin = async (email: string) => {
    setError(null);
    try {
      await accessControlService.toggleUserAdmin(email);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao alterar admin');
    }
  };

  const openEditDialog = (user: UserAccessDto) => {
    setSelectedUser(user);
    setFormDisplayName(user.displayName);
    setFormIsActive(user.isActive);
    setFormIsAdmin(user.isAdmin);
    setEditDialogOpen(true);
  };

  const openPermissionsDialog = async (user: UserAccessDto) => {
    setSelectedUser(user);
    setError(null);
    try {
      const permissions = await accessControlService.getUserPermissions(user.email);
      setUserPermissions(permissions);
      setPermissionsDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar permissoes');
    }
  };

  const handlePermissionChange = (screenKey: string, isAllowed: boolean) => {
    setUserPermissions((prev) =>
      prev.map((p) => (p.screenKey === screenKey ? { ...p, isAllowed } : p))
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setFormSaving(true);
    setError(null);
    try {
      await accessControlService.updateUserPermissions(selectedUser.email, {
        permissions: userPermissions.map((p) => ({
          screenKey: p.screenKey,
          isAllowed: p.isAllowed,
        })),
      });
      setPermissionsDialogOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Erro ao salvar permissoes');
    } finally {
      setFormSaving(false);
    }
  };

  const resetForm = () => {
    setFormEmail('');
    setFormDisplayName('');
    setFormIsActive(true);
    setFormIsAdmin(false);
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Carregando usuarios..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <PeopleTeam24Regular style={{ fontSize: '32px' }} />
          <Text as="h1" size={700} weight="semibold">
            Controle de Acesso
          </Text>
        </div>
        <Button
          appearance="primary"
          icon={<PersonAdd24Regular />}
          onClick={() => {
            resetForm();
            setCreateDialogOpen(true);
          }}
        >
          Novo Usuario
        </Button>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.searchContainer}>
        <Input
          className={styles.searchInput}
          placeholder="Buscar por nome ou email..."
          contentBefore={<Search24Regular />}
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data.value)}
        />
      </div>

      {filteredUsers.length === 0 ? (
        <Card>
          <div className={styles.emptyState}>
            Nenhum usuario encontrado. Adicione usuarios para gerenciar o acesso ao sistema.
          </div>
        </Card>
      ) : (
        <div className={styles.tableContainer}>
          <Table size="small">
            <TableHeader>
              <TableRow className={styles.tableHeaderRow}>
                <TableHeaderCell>Nome</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Último Login</TableHeaderCell>
                <TableHeaderCell>
                  <div className={styles.actionsHeader}>Acoes</div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.email}>
                  <TableCell>
                    {user.displayName}
                    {user.isAdmin && (
                      <Tooltip content="Administrador" relationship="label">
                        <ShieldPerson20Regular className={styles.adminIcon} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className={styles.statusCell}>
                      <div className={`${styles.iconWrapper} ${user.isActive ? styles.iconGreen : styles.iconGray}`}>
                        {user.isActive ? <CheckmarkCircle20Regular /> : <DismissCircle20Regular />}
                      </div>
                      <span>{user.isActive ? 'Ativo' : 'Inativo'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('pt-BR') : 'Nunca'}
                  </TableCell>
                  <TableCell>
                    <div className={styles.actions}>
                      <Tooltip content="Editar" relationship="label">
                        <Button
                          icon={<Edit24Regular />}
                          appearance="subtle"
                          onClick={() => openEditDialog(user)}
                        />
                      </Tooltip>
                      <Tooltip content="Permissoes" relationship="label">
                        <Button
                          icon={<Key24Regular />}
                          appearance="subtle"
                          onClick={() => openPermissionsDialog(user)}
                        />
                      </Tooltip>
                      <Tooltip content={user.isActive ? 'Desativar' : 'Ativar'} relationship="label">
                        <Button
                          icon={user.isActive ? <Dismiss24Regular /> : <Checkmark24Regular />}
                          appearance="subtle"
                          onClick={() => handleToggleActive(user.email)}
                        />
                      </Tooltip>
                      <Tooltip content={user.isAdmin ? 'Remover Admin' : 'Tornar Admin'} relationship="label">
                        <Button
                          icon={user.isAdmin ? <Shield24Regular /> : <ShieldTask24Regular />}
                          appearance="subtle"
                          onClick={() => handleToggleAdmin(user.email)}
                        />
                      </Tooltip>
                      <Tooltip content="Excluir" relationship="label">
                        <Button
                          icon={<Delete24Regular />}
                          appearance="subtle"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                        />
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(_, data) => setCreateDialogOpen(data.open)}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Novo Usuario</DialogTitle>
            <DialogContent>
              <div className={styles.formField}>
                <Label required>Email</Label>
                <Input
                  value={formEmail}
                  onChange={(_, data) => setFormEmail(data.value)}
                  placeholder="usuario@senacsp.edu.br"
                />
              </div>
              <div className={styles.formField}>
                <Label required>Nome</Label>
                <Input
                  value={formDisplayName}
                  onChange={(_, data) => setFormDisplayName(data.value)}
                  placeholder="Nome completo"
                />
              </div>
              <div className={styles.formField}>
                <Checkbox
                  checked={formIsActive}
                  onChange={(_, data) => setFormIsActive(data.checked === true)}
                  label="Usuario ativo"
                />
              </div>
              <div className={styles.formField}>
                <Checkbox
                  checked={formIsAdmin}
                  onChange={(_, data) => setFormIsAdmin(data.checked === true)}
                  label="Administrador"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" icon={<Dismiss24Regular />}>Cancelar</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleCreateUser}
                disabled={formSaving || !formEmail || !formDisplayName}
              >
                {formSaving ? <Spinner size="tiny" /> : 'Criar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(_, data) => setEditDialogOpen(data.open)}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogContent>
              <div className={styles.formField}>
                <Label>Email</Label>
                <Input value={selectedUser?.email || ''} disabled />
              </div>
              <div className={styles.formField}>
                <Label required>Nome</Label>
                <Input
                  value={formDisplayName}
                  onChange={(_, data) => setFormDisplayName(data.value)}
                />
              </div>
              <div className={styles.formField}>
                <Checkbox
                  checked={formIsActive}
                  onChange={(_, data) => setFormIsActive(data.checked === true)}
                  label="Usuario ativo"
                />
              </div>
              <div className={styles.formField}>
                <Checkbox
                  checked={formIsAdmin}
                  onChange={(_, data) => setFormIsAdmin(data.checked === true)}
                  label="Administrador"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" icon={<Dismiss24Regular />}>Cancelar</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleUpdateUser}
                disabled={formSaving || !formDisplayName}
              >
                {formSaving ? <Spinner size="tiny" /> : 'Salvar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onOpenChange={(_, data) => setPermissionsDialogOpen(data.open)}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Permissoes - {selectedUser?.displayName}</DialogTitle>
            <DialogContent>
              <Text>Selecione as telas que o usuario pode acessar:</Text>
              <div className={styles.permissionsList}>
                {userPermissions.map((permission) => {
                  const screen = screens.find((s) => s.key === permission.screenKey);
                  return (
                    <div key={permission.screenKey} className={styles.permissionItem}>
                      <Text>{permission.screenName}</Text>
                      <Checkbox
                        checked={permission.isAllowed}
                        onChange={(_, data) =>
                          handlePermissionChange(permission.screenKey, data.checked === true)
                        }
                        disabled={screen?.adminOnly && !selectedUser?.isAdmin}
                      />
                    </div>
                  );
                })}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" icon={<Dismiss24Regular />}>Cancelar</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleSavePermissions}
                disabled={formSaving}
              >
                {formSaving ? <Spinner size="tiny" /> : 'Salvar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogContent>
              <Text>
                Tem certeza que deseja excluir o usuario <strong>{selectedUser?.displayName}</strong>?
                Esta acao nao pode ser desfeita.
              </Text>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancelar</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Delete24Regular />}
                onClick={handleDeleteUser}
                disabled={formSaving}
              >
                {formSaving ? <Spinner size="tiny" /> : 'Excluir'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default AccessControlPage;
